import { useState, type CSSProperties } from "react";
import {
  formatCompact,
  formatPct,
  formatUSD,
  savedPct,
  type ModelRollup,
} from "../../analytics";

/* Rows shown before the list collapses behind "Show all". */
const COLLAPSED_ROWS = 3;

/* Rows visible before the expanded list scrolls — same row metrics as the
 * agents roster, minus the 33px logo (models have no brand mark), so the row is
 * shorter. */
const VISIBLE_ROWS = 6;
const ROW_PX = 57;

/* Shared column template so the bordered rows align as one table:
 * name | calls | spend | tokens | saved */
const ROW_GRID =
  "grid grid-cols-[minmax(150px,1.9fr)_minmax(80px,0.5fr)_72px_72px_120px] items-center gap-4";

/* Models across all agents — the per-model usage breakdown (OPSS-421), a sibling
 * of ConnectedAgentsCard: same section header, same individually bordered rounded
 * rows, same collapse behaviour. Where the agents roster answers "which of my
 * agents is spending", this answers "on which models", pooled over every agent.
 *
 * Rows are traffic-derived, so unlike the agents roster there is no idle/zero
 * row: a model with no traffic in the window simply is not listed.
 *
 * Read-only on purpose. Rows are not click-to-filter: an agent row navigates to
 * that agent, so a model row that instead mutated the page's filters would be
 * the same gesture doing two different things in one list. Narrowing to a model
 * is the Model menu's job. */
export function ModelsCard({
  models,
  loading = false,
}: {
  models: ModelRollup[];
  /** True before the first rollups load — show a skeleton row set. */
  loading?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? models : models.slice(0, COLLAPSED_ROWS);

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <p
          className="cursor-default text-[15px] leading-none"
          style={{ color: "var(--text)" }}
        >
          Models used across all agents{" "}
          <span style={{ color: "var(--text-muted)" }}>({models.length})</span>
        </p>
      </div>
      {loading && models.length === 0 ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[49px] animate-pulse rounded-[5px] bg-[var(--surface-2)]"
            />
          ))}
        </div>
      ) : models.length === 0 ? (
        <p className="text-[13px]" style={{ color: "var(--text-muted)" }}>
          No model usage in this range.
        </p>
      ) : (
        <div
          className="flex flex-col gap-2 overflow-y-auto"
          style={
            expanded && models.length > VISIBLE_ROWS
              ? { maxHeight: VISIBLE_ROWS * ROW_PX }
              : undefined
          }
        >
          {rows.map((m, i) => (
            <ModelRow
              key={m.model}
              model={m}
              floatDelay={i < COLLAPSED_ROWS ? 0.15 + i * 0.05 : null}
            />
          ))}
        </div>
      )}

      {models.length > COLLAPSED_ROWS && (
        <div className="mt-3 flex justify-center">
          <button
            onClick={() => setExpanded((e) => !e)}
            className="flex items-center gap-1 text-[13px] transition-colors hover:text-[var(--text)]"
            style={{ color: "var(--text-muted)" }}
          >
            {expanded ? "Show less" : "Show all"}
          </button>
        </div>
      )}
    </section>
  );
}

function ModelRow({
  model: m,
  floatDelay,
}: {
  model: ModelRollup;
  /** Entrance stagger (seconds), or null to render without the animation. */
  floatDelay: number | null;
}) {
  const tokens =
    m.tokens.input +
    m.tokens.output +
    m.tokens.cache_read +
    m.tokens.cache_write;
  // A model whose calls nearly all failed earned no tokens and so cost almost
  // nothing — without saying so, it reads as remarkably cheap rather than
  // broken. Staging really serves one at 304 calls / 303 failed / $0.
  const mostlyFailed = m.call_count > 0 && m.failed_calls / m.call_count >= 0.5;

  return (
    <div
      className={`${floatDelay !== null ? "tw-float-up" : ""} shrink-0 cursor-default rounded-[5px] border border-[var(--border)] px-5 py-[13px] ${ROW_GRID}`}
      style={
        floatDelay !== null
          ? ({ "--float-delay": `${floatDelay}s` } as CSSProperties)
          : undefined
      }
    >
      <div className="min-w-0">
        <p
          className="truncate text-[13px] font-medium leading-tight"
          style={{ color: "var(--text)" }}
          title={m.model}
        >
          {m.model}
        </p>
        {mostlyFailed && (
          <p
            className="mt-0.5 truncate text-[11px] leading-tight"
            style={{ color: "var(--text-muted)" }}
          >
            {formatCompact(m.failed_calls)} of {formatCompact(m.call_count)}{" "}
            calls failed
          </p>
        )}
      </div>
      <MonoStat value={formatCompact(m.call_count)} label="Calls" />
      {m.cost.total ? (
        <MonoStat value={formatUSD(m.cost.total)} label="Spend" />
      ) : (
        <span />
      )}
      {tokens ? (
        <MonoStat value={formatCompact(tokens)} label="Tokens" />
      ) : (
        <span />
      )}
      {m.savings_usd > 0 ? (
        <span
          className="tw-mono justify-self-start rounded-full px-2.5 py-1 text-[10px] font-medium uppercase leading-none tracking-[0.25px]"
          style={{
            // Same fixed scheme as the agents roster and the Savings card.
            background: "var(--savings-card-bg)",
            color: "var(--savings-fg)",
          }}
        >
          <span className="tabular-nums">
            {formatPct(savedPct(m.savings_usd, m.cost.total))}
          </span>{" "}
          saved
        </span>
      ) : (
        <span />
      )}
    </div>
  );
}

function MonoStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span
        className="tw-mono text-[11px] font-medium leading-none tabular-nums"
        style={{ color: "var(--text)" }}
      >
        {value}
      </span>
      <span
        className="tw-mono text-[10px] uppercase leading-none tracking-[0.25px]"
        style={{ color: "var(--text-muted)" }}
      >
        {label}
      </span>
    </div>
  );
}
